import type { ZodType } from 'zod'
import { ApiErrorEnvelopeSchema, ApiSuccessEnvelopeSchema } from '@mist/contracts/api'
import { SUBSCRIBER_SESSION_CSRF_HEADER } from '@mist/contracts/sessions'

/**
 * Same-origin typed API client (R-ARCH-03, Section 9.1).
 *
 * Browser calls hit the relative `/api/v1/*` path, which the Cloudflare/Next
 * rewrite proxies to Railway so session cookies stay first-party; direct
 * cross-site browser calls are prohibited. Server components call the API
 * origin directly (server-to-server), which is not a browser call and does not
 * violate the same-origin rule. Responses are validated against the Section 9.1
 * envelope so malformed upstream data can never masquerade as a typed success.
 */
const isBrowser = typeof window !== 'undefined'


/** A user-safe API failure. Carries only the safe envelope fields (Section 9.1). */
export class ApiError extends Error {
  readonly code: string
  readonly requestId?: string
  readonly retryable: boolean
  readonly status: number
  readonly fieldErrors: ReadonlyArray<{ field: string; code: string; message: string }>

  constructor(args: {
    code: string
    message: string
    retryable: boolean
    status: number
    requestId?: string
    fieldErrors?: ReadonlyArray<{ field: string; code: string; message: string }>
  }) {
    super(args.message)
    this.name = 'ApiError'
    this.code = args.code
    if (args.requestId !== undefined) this.requestId = args.requestId
    this.retryable = args.retryable
    this.status = args.status
    this.fieldErrors = args.fieldErrors ?? []
  }
}

function resolveUrl(path: string): string {
  if (isBrowser) return path
  const origin = process.env.MIST_API_ORIGIN ?? 'http://127.0.0.1:8787'
  return new URL(path, origin).toString()
}

export interface ApiRequestOptions {
  readonly csrfToken?: string
  readonly signal?: AbortSignal
  readonly cache?: RequestCache
  readonly revalidateSeconds?: number
  readonly referrerPolicy?: ReferrerPolicy
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  schema: ZodType<T>,
  body: unknown,
  options: ApiRequestOptions,
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' }
  if (body !== undefined) headers['content-type'] = 'application/json'
  if (options.csrfToken) headers[SUBSCRIBER_SESSION_CSRF_HEADER] = options.csrfToken

  const init: RequestInit & { next?: { revalidate: number } } = {
    method,
    headers,
    credentials: 'include',
  }
  if (body !== undefined) init.body = JSON.stringify(body)
  if (options.signal) init.signal = options.signal
  if (options.cache) init.cache = options.cache
  if (typeof options.revalidateSeconds === 'number') init.next = { revalidate: options.revalidateSeconds }
  if (options.referrerPolicy) init.referrerPolicy = options.referrerPolicy

  let response: Response
  try {
    response = await fetch(resolveUrl(path), init)
  } catch {
    throw new ApiError({ code: 'NETWORK', message: 'We could not reach the service. Please try again.', retryable: true, status: 0 })
  }

  const raw: unknown = await response.json().catch(() => undefined)

  if (!response.ok) {
    const parsed = ApiErrorEnvelopeSchema.safeParse(raw)
    if (parsed.success) {
      throw new ApiError({
        code: parsed.data.error.code,
        message: parsed.data.error.message,
        retryable: parsed.data.error.retryable,
        status: response.status,
        requestId: parsed.data.request_id,
        fieldErrors: parsed.data.error.details ?? [],
      })
    }
    throw new ApiError({ code: 'UNEXPECTED', message: 'Something went wrong. Please try again.', retryable: true, status: response.status })
  }

  const envelope = ApiSuccessEnvelopeSchema.parse(raw)
  return schema.parse(envelope.data)
}

export function getApi<T>(path: string, schema: ZodType<T>, options: ApiRequestOptions = {}): Promise<T> {
  return request('GET', path, schema, undefined, options)
}

export function postApi<T>(path: string, schema: ZodType<T>, body: unknown, options: ApiRequestOptions = {}): Promise<T> {
  return request('POST', path, schema, body, options)
}
